<template>
    <component v-if="!htmlText" :is="tag" :style="style">
        <slot></slot>
    </component>
    <component v-else :is="tag" :style="style" v-html="htmlText" />
</template>

<script>
import {
    fontWeight,
    textAligns,
    textDecorations,
    textTransformations,
    getStorybookDefaults,
} from '@/constants';

import { lineHeightRange } from './Typography.validation';

export function getTypographyStorybookControls(
    componentName,
    categoryName = ''
) {
    const category = categoryName || componentName;

    return {
        [`${componentName}Styles`]: {
            table: {
                disable: true,
            },
        },
        [`${componentName}Size`]: {
            name: 'Size',
            table: { category },
            type: { name: 'string' },
        },
        [`${componentName}Tag`]: {
            name: 'HTML Tag',
            table: { category },
            options: Object.values(typographyTags),
            control: { type: 'select', labels: typographyTags },
        },
        [`${componentName}Color`]: {
            name: 'Color',
            control: { type: 'color' },
            table: { category },
        },
        [`${componentName}Weight`]: {
            name: 'Weight',
            table: { category },
            options: Object.values(fontWeight),
            control: { type: 'select', labels: fontWeight },
        },
        [`${componentName}Decoration`]: {
            name: 'Decoration',
            table: { category },
            options: Object.values(textDecorations),
            control: { type: 'select', labels: textDecorations },
        },
        [`${componentName}LineHeight`]: {
            name: 'Line Height',
            table: { category },
            type: { name: 'string' },
            description: `Valid range: ${lineHeightRange[0]} - ${lineHeightRange[1]}`,
        },
        [`${componentName}TextTransform`]: {
            name: 'Text Transform',
            table: { category },
            options: Object.values(textTransformations),
            control: { type: 'select', labels: textTransformations },
        },
        [`${componentName}TextAlign`]: {
            name: 'Text Alignment',
            table: { category },
            options: Object.values(textAligns),
            control: { type: 'select', labels: textAligns },
        },
    };
}

export function getTypographyProps(props, componentName) {
    return {
        size: props[componentName + 'Size'],
        tag: props[componentName + 'Tag'],
        color: props[componentName + 'Color'],
        weight: props[componentName + 'Weight'],
        decoration: props[componentName + 'Decoration'],
        lineHeight: props[componentName + 'LineHeight'],
        textTransform: props[componentName + 'TextTransform'],
        textAlign: props[componentName + 'TextAlign'],
    };
}

export function getTypographyStorybookHelpers(
    componentName,
    categoryName = '',
    defaultStyles
) {
    return {
        controls: getTypographyStorybookControls(componentName, categoryName),
        defaults: defaultStyles
            ? getStorybookDefaults(componentName, defaultStyles)
            : {},
    };
}

export const typographyTags = {
    span: 'span',
    p: 'p',
    h1: 'h1',
    h2: 'h2',
    h3: 'h3',
    h4: 'h4',
    h5: 'h5',
    h6: 'h6',
    div: 'div',
    li: 'li',
};

function calcLineHeight(val) {
    let newLh = val ?? 'normal';
    if (newLh.includes('px')) {
        newLh = newLh.replace('px', '') * 1.4 + 'px';
    }
    return newLh;
}

export default {
    name: 'Typography',
    props: {
        htmlText: {
            type: String,
            default: null,
        },
        tag: {
            type: String,
            default: typographyTags.span,
        },
        size: {
            type: String,
            default: '14px',
        },
        color: {
            type: String,
            default: '#000',
        },
        weight: {
            type: String,
            default: fontWeight.medium,
        },
        decoration: {
            type: String,
            default: textDecorations.none,
        },
        lineHeight: {
            type: String,
            default: 'normal',
        },
        textTransform: {
            type: String,
            default: textTransformations.none,
        },
        textAlign: {
            type: String,
            default: textAligns.none,
        },
    },
    computed: {
        style() {
            return {
                color: this.color,
                fontWeight: this.weight,
                fontSize: this.size,
                textDecoration: this.decoration,
                lineHeight: calcLineHeight(this.lineHeight),
                textTransform: this.textTransform,
                textAlign: this.textAlign,
            };
        },
    },
};
</script>
